import { useEffect } from 'react';
import { useFileManagerStore } from '../store/useFileManagerStore';
import { processDropItems } from '../utils/fileUtils';
import { eventBus } from '../../../core/EventBus';

export const useKeyboardShortcuts = () => {
  const {
    selectedPaths,
    files,
    deleteFiles,
    copyFiles,
    startRenaming,
    pasteFiles,
    currentPath,
    refresh,
    uploadFiles
  } = useFileManagerStore();

  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
        // If pasting text into an input, ignore
        if (document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement) return;

        // Try to handle items via webkitGetAsEntry if available (for folders)
        // Note: Browser support for folder paste is inconsistent.
        if (e.clipboardData && e.clipboardData.items && e.clipboardData.items.length > 0) {
            // Check if there are any file-like items
            const hasFiles = Array.from(e.clipboardData.items).some(item => item.kind === 'file');
            
            if (hasFiles) {
                e.preventDefault();
                // Attempt to process items recursively like drag-drop
                const flatFiles = await processDropItems(e.clipboardData.items);
                if (flatFiles.length > 0) {
                    uploadFiles(flatFiles);
                    return;
                }
            }
        }

        // Fallback to legacy files list if items processing failed or empty
        if (e.clipboardData && e.clipboardData.files.length > 0) {
            e.preventDefault();
            const files = Array.from(e.clipboardData.files);
            uploadFiles(files.map(f => ({ file: f })));
        } else {
            // Internal app paste
            pasteFiles();
        }
    };


    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if input is focused
      if (document.activeElement instanceof HTMLInputElement) return;

      if (e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'c': {
            e.preventDefault();
            copyFiles(false);
            // 同时触发"打包下载"通知（复用远程剪贴板下载流程）
            const selectedFiles = files.filter(f => selectedPaths.includes(f.path));
            if (selectedFiles.length > 0) {
              const totalSize = selectedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
              eventBus.emit('clipboard:filesAvailable', {
                files: selectedFiles.map(f => ({
                  name: f.name,
                  path: f.path,
                  size: f.size || 0,
                  fileCount: 1,
                  isDir: f.type === 'dir',
                })),
                totalSize,
                fileCount: selectedFiles.length,
              });
            }
            break;
          }
          case 'x':
            e.preventDefault();
            copyFiles(true);
            break;
          case 'a':
            e.preventDefault();
            // TODO: Select all logic
            break;
          case 'r':
            e.preventDefault();
            refresh();
            break;
        }
      } else {
        switch (e.key) {
          case 'Delete':
            if (selectedPaths.length > 0) deleteFiles(selectedPaths);
            break;
          case 'F2':
            if (selectedPaths.length === 1) startRenaming(selectedPaths[0]);
            break;
          case 'F5':
            e.preventDefault();
            refresh();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('paste', handlePaste);
    return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('paste', handlePaste);
    };
  }, [selectedPaths, files, currentPath, deleteFiles, copyFiles, startRenaming, pasteFiles, refresh, uploadFiles]);
};
